import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Each deploy emits new content-hashed chunks and deletes the old ones. A browser still
 * holding the previous `index.html` asks for a chunk that no longer exists and gets a 404 —
 * and NO ErrorBoundary can fix that by re-rendering, because the module will never load.
 * The only repair is a full reload onto the new `index.html`.
 */
const RELOAD_GUARD_KEY = 'lfour:chunk-reloaded';

/**
 * The guard is bounded, not permanent: a tab left open across two deploys must still be able
 * to recover from the second. Clearing it on render instead would loop while a CDN is still
 * serving the stale `index.html`.
 */
const RELOAD_GUARD_MS = 10 * 60 * 1000;

/** The three shapes this failure takes across Chrome, Firefox and Safari. */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed'
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const lower = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
}

/** If the reload itself fails the same way, show the error instead of looping. */
function reloadOnce(): boolean {
  const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY));
  if (last && Date.now() - last < RELOAD_GUARD_MS) return false;
  window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
  return true;
}

/** Same guard for any dynamic `import()`, component or not. */
export async function withChunkRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory();
  } catch (error) {
    if (isChunkLoadError(error) && reloadOnce()) {
      // The reload is in flight; never resolve, so nothing renders a half-torn tree.
      return await new Promise<T>(() => {});
    }
    throw error;
  }
}

export type PreloadableComponent<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload: () => Promise<{ default: T }>;
};

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): PreloadableComponent<T> {
  let inFlight: Promise<{ default: T }> | null = null;
  // One promise for React and for any warm-up, so a prefetched chunk is never fetched twice.
  const load = () => {
    inFlight ??= withChunkRetry(factory).catch((error) => {
      inFlight = null;
      throw error;
    });
    return inFlight;
  };
  const Component = lazy(load) as PreloadableComponent<T>;
  Component.preload = load;
  return Component;
}

/**
 * Vite fires `vite:preloadError` for a module preload that 404s — it happens BEFORE any
 * component tries to render, so `lazyWithRetry` alone would never see it. Called once in
 * `main.tsx`.
 */
export function installPreloadErrorReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    reloadOnce();
  });
}
